const API_BASE_URL = (process.env.VUE_APP_RECHARGE_API_BASE_URL || 'https://raya.smnm.xyz/api/v1').replace(/\/$/, '')

const ORDER_PATHS = {
  payermax: '/payment/order/payermax/create',
  spancash: '/payment/external/order/spancash/create',
  ugamedeal: '/payment/external/order/ugamedeal/create',
  epay: '/payment/external/order/epay/create',
  jollyepin: '/payment/external/order/jollyepin/create',
  deeppay: '/payment/external/order/deeppay/create',
  virgo: '/payment/external/order/virgo/create',
  cregis: '/payment/external/order/cregis/create'
}

export class RechargeApiError extends Error {
  constructor(message, code, status) {
    super(message)
    this.name = 'RechargeApiError'
    this.code = code
    this.status = status
  }
}

function unwrap(payload) {
  return payload && Object.prototype.hasOwnProperty.call(payload, 'data')
    ? payload.data
    : payload
}

async function request(path, options = {}) {
  const headers = {
    Accept: 'application/json',
    ...(options.body ? { 'Content-Type': 'application/json' } : {}),
    ...(options.token ? { H5Token: encodeURIComponent(options.token).replace(/%20/g, '%2B') } : {}),
    ...(options.rechargeToken ? { 'X-Recharge-Token': options.rechargeToken } : {})
  }

  let response
  try {
    response = await fetch(`${API_BASE_URL}${path}`, {
      method: options.method || 'GET',
      headers,
      body: options.body ? JSON.stringify(options.body) : undefined
    })
  } catch (error) {
    throw new RechargeApiError('Recharge service is unavailable. Please try again.', 'NETWORK_ERROR')
  }

  let payload = null
  try {
    payload = await response.json()
  } catch (error) {
    payload = null
  }

  const message = payload && (payload.message || payload.msg)
  if (!response.ok) {
    throw new RechargeApiError(message || 'Recharge request was rejected.', payload && payload.code, response.status)
  }
  if (payload && payload.code != null && Number(payload.code) !== 200) {
    throw new RechargeApiError(message || 'Recharge request was rejected.', payload.code, response.status)
  }
  return unwrap(payload)
}

function query(params) {
  return new URLSearchParams(
    Object.entries(params).filter(([, value]) => value !== undefined && value !== null && value !== '')
  ).toString()
}

export function exchangeHandoff(ticket) {
  return request(`/payment/recharge/h5/exchange?${query({ ticket })}`, { method: 'POST' })
}

export function getCapabilities({ distribution = 'web', platform = 'web', country, currency = 'USD' } = {}) {
  return request(`/payment/recharge/capabilities?${query({ distribution, platform, country, currency })}`)
}

export function getProducts({ channel, country, currency = 'USD' } = {}) {
  return request(`/payment/recharge/products?${query({ channel, country, currency })}`)
}

export function getUserByDanaId(danaId, token) {
  return request(`/payment/recharge/h5/user?${query({ danaId })}`, { rechargeToken: token })
}

export function createProviderOrder({ channel, method, product, userId, country, currency, phone, email, token }) {
  const path = ORDER_PATHS[channel]
  if (!path) throw new RechargeApiError('This payment channel is not available.', 'UNSUPPORTED_CHANNEL')

  const body = {
    uid: String(userId),
    goodsId: product.id,
    goodsType: 1,
    subject: 'order',
    country,
    frontCallbackUrl: `${window.location.origin}${window.location.pathname}`
  }
  if (channel === 'spancash') body.paymentMethod = method
  if (channel === 'ugamedeal') {
    body.currencyCode = currency
    body.paymentMethod = method
  }
  if (channel === 'deeppay' || channel === 'virgo') {
    body.amount = Math.round(Number(product.price) * 9534)
    body.inputPhone = phone
    body.inputEmail = email
  }
  if (channel === 'cregis') {
    // Dana prices the pack, signs the Cregis call and owns the return URLs; this
    // page only says which pack, which chain and who is paying. The recharge
    // session token is the only credential a crypto order accepts.
    body.payToken = method
    body.payerEmail = email
    return request(path, { method: 'POST', body, rechargeToken: token })
  }

  return request(path, { method: 'POST', body, token })
}

/** Current state of a crypto order, polled while the player sends the deposit. */
export function getCryptoOrder(orderId, token) {
  return request(`/payment/external/order/cregis/order/${encodeURIComponent(orderId)}`, { rechargeToken: token })
}

export function checkoutUrl(data) {
  if (typeof data === 'string') return data
  if (!data || typeof data !== 'object') return ''
  return data.checkoutUrl || data.checkout_url || data.paymentUrl || data.url || data.redirectUrl || ''
}
